<template>
	<GlassCard class="p-4">
		<h2 class="mb-4">Custom Report</h2>
		<div class="d-flex align-items-end gap-3 mb-4 flex-wrap">
			<div>
				<label class="form-label mb-1">Report Type</label>
				<select v-model="selectedType" class="form-select">
					<option value="health-provider">Health Provider Export</option>
				</select>
			</div>
			<div>
				<label class="form-label mb-1">Date Range</label>
				<div
					class="date-range-group d-flex gap-2 align-items-center flex-wrap flex-md-nowrap">
					<input
						type="date"
						v-model="dateRange.start"
						class="form-control"
						style="min-width: 150px" />
					<span class="date-range-sep">to</span>
					<input
						type="date"
						v-model="dateRange.end"
						class="form-control"
						style="min-width: 150px" />
				</div>
			</div>
			<button
				class="btn btn-orange"
				@click="generateReport"
				:disabled="generating || loading">
				<span v-if="generating"
					><span class="spinner-border spinner-border-sm me-2"></span
					>Generating...</span
				>
				<span v-else>Generate Report</span>
			</button>
		</div>
		<table class="table table-bordered table-hover align-middle">
			<thead class="table-light">
				<tr>
					<th>Report Type</th>
					<th>Date Range</th>
					<th>Status</th>
				</tr>
			</thead>
			<tbody>
				<tr v-if="loading">
					<td colspan="3" class="text-center text-muted">Loading...</td>
				</tr>
				<tr v-for="report in paginatedReports" :key="report.id" v-else>
					<td>{{ report.report_type }}</td>
					<td>{{ report.dateRange }}</td>
					<td>
						<button
							v-if="report.status === 'done'"
							class="btn btn-link p-0"
							title="Download"
							@click="downloadReport(report)">
							<i class="bi bi-arrow-down-circle" style="font-size: 1.5rem"></i>
						</button>
						<span
							v-else-if="report.status === 'pending'"
							class="badge bg-warning text-dark"
							>Pending</span
						>
						<span
							v-else-if="report.status === 'generating'"
							class="badge bg-info text-dark"
							>Generating</span
						>
					</td>
				</tr>
				<tr v-if="!loading && paginatedReports.length === 0">
					<td colspan="3" class="text-center text-muted">No reports yet.</td>
				</tr>
			</tbody>
		</table>
		<div class="d-flex justify-content-center mt-3">
			<nav aria-label="Report pagination">
				<ul class="pagination mb-0 flex-wrap">
					<li class="page-item" :class="{ disabled: currentPage === 1 }">
						<button
							class="page-link"
							@click="goToFirst"
							:disabled="currentPage === 1">
							First
						</button>
					</li>
					<li class="page-item" :class="{ disabled: currentPage === 1 }">
						<button
							class="page-link"
							@click="goToPrev"
							:disabled="currentPage === 1">
							Prev
						</button>
					</li>
					<li
						v-for="page in pageNumbers()"
						:key="page + '-pg'"
						class="page-item"
						:class="{ active: currentPage === page, disabled: page === '...' }">
						<span v-if="page === '...'" class="page-link">&hellip;</span>
						<button v-else class="page-link" @click="goToPage(page)">
							{{ page }}
						</button>
					</li>
					<li
						class="page-item"
						:class="{ disabled: currentPage >= totalPages }">
						<button
							class="page-link"
							@click="goToNext"
							:disabled="currentPage >= totalPages">
							Next
						</button>
					</li>
					<li
						class="page-item"
						:class="{ disabled: currentPage >= totalPages }">
						<button
							class="page-link"
							@click="goToLast"
							:disabled="currentPage >= totalPages">
							Last
						</button>
					</li>
				</ul>
			</nav>
		</div>
		<div class="report-explainer mt-4">
			<p class="mb-2">
				The Health Provider report is a shareable export of the information you
				log in Grub Vision, including meals, drinks, weight entries, and blood
				sugar readings for the selected date range.
			</p>
			<p class="mb-0 small">
				This export is intended to support conversations with a licensed
				clinician. It is not a diagnosis, treatment plan, or a substitute for
				professional medical advice.
			</p>
		</div>
	</GlassCard>
</template>

<script setup>
	import { ref, computed, watch, onMounted } from "vue"
	import GlassCard from "./GlassCard.vue"
	import moment from "moment-timezone"
	import { fetchWithAuth } from "@/utils/api-client"
	import Swal from "sweetalert2"
	// import * as SubscriptionHelper from "@/utils/subscriptionHelper"
	import getUrl from "@/utils/url"

	const selectedType = ref("health-provider")
	const dateRange = ref({
		start: moment().subtract(1, "month").format("YYYY-MM-DD"),
		end: moment().format("YYYY-MM-DD"),
	})

	const reports = ref([])
	const total = ref(0)
	const pageSize = 10
	const currentPage = ref(1)
	const totalPages = computed(() => Math.ceil(total.value / pageSize))
	const loading = ref(false)
	const generating = ref(false)

	async function downloadReport(report) {
		if (!report.file_url) {
			Swal.fire({
				icon: "error",
				title: "No file available",
				text: "This report does not have a downloadable file yet.",
			})
			return
		}
		const url = getUrl("fetch") + "/pdf" + report.file_url
		window.open(url, "_blank")
	}

	async function fetchReports(page = 1) {
		loading.value = true
		try {
			const response = await fetchWithAuth(
				`/api/report/custom-reports?page=${page}&pageSize=${pageSize}`
			)
			if (!response.ok) throw new Error("Failed to fetch reports")
			const data = await response.json()
			reports.value = data.reports
			total.value = data.total
			currentPage.value = data.page
		} catch (e) {
			reports.value = []
			total.value = 0
		} finally {
			loading.value = false
		}
	}

	onMounted(() => {
		fetchReports(1)
	})

	watch(currentPage, (page) => {
		fetchReports(page)
	})

	const paginatedReports = computed(() =>
		reports.value.map((r) => ({
			...r,
			dateRange:
				r.start_date && r.end_date ? `${r.start_date} to ${r.end_date}` : "",
		}))
	)

	async function generateReport() {
		const feature = "Generate custom report"
		// if (SubscriptionHelper.isFreeAccount()) {
		// 	SubscriptionHelper.showLockedModal(feature)
		// 	return
		// }
		// Check that the date range does not exceed 3 months (90 days)
		const start = moment(dateRange.value.start, "YYYY-MM-DD")
		const end = moment(dateRange.value.end, "YYYY-MM-DD")
		if (end.diff(start, "days") > 90) {
			Swal.fire({
				icon: "error",
				title: "Date Range Too Large",
				text: "You can only generate a report for up to 3 months at a time.",
			})
			return
		}
		generating.value = true
		try {
			const response = await fetchWithAuth("/api/report/generate", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					report_type: selectedType.value,
					start_date: dateRange.value.start,
					end_date: dateRange.value.end,
				}),
			})
			// if (
			// 	await SubscriptionHelper.requestCheckSubscription(
			// 		response,
			// 		"Custom Reports"
			// 	)
			// ) {
			if (!response.ok) throw new Error("Failed to generate report")
			await fetchReports(1)
			// }
		} catch (e) {
			Swal.fire({
				icon: "error",
				title: "Error",
				text: e.message,
			})
		} finally {
			generating.value = false
		}
	}

	function goToPage(page) {
		if (page < 1 || page > totalPages.value) return
		currentPage.value = page
	}

	function goToFirst() {
		currentPage.value = 1
	}
	function goToLast() {
		currentPage.value = totalPages.value
	}
	function goToPrev() {
		if (currentPage.value > 1) currentPage.value--
	}
	function goToNext() {
		if (currentPage.value < totalPages.value) currentPage.value++
	}

	function pageNumbers() {
		const total = totalPages.value
		const current = currentPage.value
		if (total <= 7) {
			return Array.from({ length: total }, (_, i) => i + 1)
		}
		const pages = []
		// Always show first 2
		pages.push(1, 2)
		// Pages around current
		let start = Math.max(3, current - 1)
		let end = Math.min(total - 2, current + 1)
		if (start > 3) pages.push("...")
		for (let i = start; i <= end; i++) {
			pages.push(i)
		}
		if (end < total - 2) pages.push("...")
		// Always show last 2
		pages.push(total - 1, total)
		// Remove duplicates
		return pages.filter((v, i, a) => a.indexOf(v) === i)
	}
</script>

<style scoped>
	.report-explainer {
		border: 1px solid rgba(255, 255, 255, 0.18);
		border-radius: 14px;
		background: rgba(255, 255, 255, 0.06);
		padding: 1rem 1.1rem;
	}
	.btn-orange {
		background-color: #ff6400;
		color: white;
	}
	.btn-orange:hover {
		background-color: #ff6400;
		color: white;
		opacity: 0.9;
	}
	.date-range-group {
		width: 100%;
	}
	@media (max-width: 600px) {
		.date-range-group {
			flex-direction: column !important;
			align-items: stretch !important;
			gap: 0.25rem !important;
		}
		.date-range-sep {
			display: none;
		}
	}
</style>
